import React, { useState } from 'react';
import { Button, Typography } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { CancelRequestModal } from './CancelRequestModal';
import { useHomeStrings } from '../../hooks/useHomeStrings';
import { formatDateShort } from '../../utils/formatDate';
import { formatTime } from '../../utils/formatTime';
import type { HomeUpcomingBooking } from '../../model/types';

interface Props {
  booking: HomeUpcomingBooking;
}

export const UpcomingBookingCard: React.FC<Props> = ({ booking }) => {
  const STRINGS = useHomeStrings();
  const navigate = useNavigate();
  const [cancelOpen, setCancelOpen] = useState(false);

  const isConfirmed = booking.status === 'CONFIRMED';
  const statusLabel = isConfirmed ? STRINGS.BOOKING_STATUS_CONFIRMED : STRINGS.BOOKING_STATUS_PENDING;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={`${booking.serviceName}, ${formatDateShort(booking.startAt)} ${formatTime(booking.startAt)}`}
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(`/bookings/${booking.id}`);
          }
        }}
        style={{
          margin: '0 16px 12px',
          borderRadius: 'var(--bl-radius-lg)',
          background: 'var(--bl-surface)',
          boxShadow: 'var(--bl-shadow)',
          border: '1px solid var(--bl-divider)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          cursor: 'pointer',
        }}
        onClick={() => navigate(`/bookings/${booking.id}`)}
      >
        {/* Left accent bar */}
        <div style={{ width: 4, background: 'var(--bl-primary)', flexShrink: 0 }} />

        {/* Content */}
        <div style={{ flex: 1, padding: '13px 14px' }}>
          {/* Section label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
            <CalendarOutlined style={{ fontSize: 12, color: 'var(--bl-text-secondary)' }} />
            <Typography.Text
              style={{ fontSize: 12, color: 'var(--bl-text-secondary)', fontWeight: 500 }}
            >
              {STRINGS.UPCOMING_SECTION_LABEL}
            </Typography.Text>
          </div>

          {/* Service + status */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 8,
            }}
          >
            <Typography.Text
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--bl-text)',
                flex: 1,
                minWidth: 0,
              }}
            >
              {booking.serviceName}
            </Typography.Text>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: isConfirmed ? 'var(--bl-primary)' : 'var(--bl-text-secondary)',
                background: 'var(--bl-divider)',
                borderRadius: 20,
                padding: '3px 10px',
                flexShrink: 0,
              }}
            >
              {statusLabel}
            </span>
          </div>

          {/* Provider */}
          {booking.providerName && (
            <Typography.Text
              style={{
                display: 'block',
                fontSize: 13,
                color: 'var(--bl-text-secondary)',
                marginTop: 3,
              }}
            >
              {booking.providerName}
            </Typography.Text>
          )}

          {/* Date/time block */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              background: 'var(--bl-divider)',
              borderRadius: 10,
              padding: '8px 12px',
              marginTop: 10,
            }}
          >
            <Typography.Text style={{ fontSize: 15, fontWeight: 700, color: 'var(--bl-primary)' }}>
              {formatDateShort(booking.startAt)}, {formatTime(booking.startAt)}
            </Typography.Text>
          </div>

          {/* Cancel button — shown only when cancellation is allowed */}
          {booking.canRequestCancel && (
            <div
              style={{ marginTop: 10 }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Button
                danger
                onClick={() => setCancelOpen(true)}
                style={{ width: '100%', height: 44, borderRadius: 'var(--bl-radius-sm)', fontWeight: 600, fontSize: 13 }}
              >
                {STRINGS.BOOKING_CANCEL}
              </Button>
            </div>
          )}
        </div>
      </div>

      <CancelRequestModal
        bookingUuid={booking.bookingUuid}
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
      />
    </>
  );
};
